import { useId, useState, useRef, useEffect, useMemo, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import MuiAutocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CheckboxField from '../../../components/form/CheckboxField';
import NumberField from '../../../components/form/NumberField';
import AppSelectBox from '../../../components/shared/AppSelectBox';
import AppTextBox from '../../../components/shared/AppTextBox';
import DateField from '../../../components/form/DateField';
import TimeField from '../../../components/form/TimeField';
import type { TwoDigitYear } from '../../../components/form/dateUtils';
import { argbToHex, hexToArgb } from '../../../types/facility';
import HelpTooltip from '../../../components/common/HelpTooltip';
import Field from '../../../components/form/Field';
import { borderLabel, useLabelPlacement, type LabelPlacement } from '../../../components/form/labelPlacement';
import { measureText } from '../../../components/grid/model';
// IMPORTANT: these controls rely on .settings-input / .settings-select / .settings-field /
// .settings-subgroup / .settings-spin-* / .settings-checkbox styles. Import the stylesheet
// alongside the components so any page that uses them gets the styling automatically,
// regardless of whether FacilitySettings.tsx has been loaded first.
import '../FacilitySettings.css';

/**
 * The label slot a wrapper keeps when its caller passes `label=""` and draws
 * the visible label itself: the same empty <label> the wrappers always
 * rendered, so those layouts keep their spacing. Required marker and help
 * stay with it, as before.
 */
function OuterLabelSlot({ id, required, helpText }: { id: string; required?: boolean; helpText?: string }) {
  return (
    <label className="settings-field-label" htmlFor={id}>
      {required && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}
      {helpText && <HelpTooltip text={helpText} />}
    </label>
  );
}

/**
 * The control and, beside it, its help icon. The label itself is the
 * field's (it follows the label placement setting), so help can no longer
 * ride inside it. `fixed` keeps a fixed-width control (time, number) at its
 * width instead of stretching it.
 */
function FieldRow({ helpText, fixed, children }: { helpText?: string; fixed?: boolean; children: React.ReactNode }) {
  if (!helpText && !fixed) return <>{children}</>;
  return (
    <div className={`settings-field-row${fixed ? ' settings-field-row--fixed' : ''}`}>
      {children}
      {helpText && <HelpTooltip text={helpText} />}
    </div>
  );
}

/**
 * Checkbox. Renders the shared `CheckboxField` (MUI).
 *
 * The `.settings-checkbox` wrapper is kept deliberately: TubeFeedTab and
 * responsive.css both hook that class for layout and touch sizing.
 * CheckboxField's own <label> handles click-to-toggle and the labelling.
 *
 * `onChange` fires only when a person ticks or unticks the box. The
 * DevExtreme CheckBox this replaced also fired it whenever `checked` or
 * `indeterminate` changed from outside, so loading a record echoed every
 * flag back as an edit (and a "select all" turning indeterminate reported
 * false).
 */
export function SettingsCheckbox({ label, checked, onChange, disabled, helpText, indeterminate, ariaLabel }: {
  label: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; helpText?: string;
  /** Render the mixed state — for "select all" headers where only some
   *  children are checked. DevExtreme showed this for a null value. */
  indeterminate?: boolean;
  /** Accessible name when `label` is empty (the control sits in markup
   *  that supplies its own visible text). */
  ariaLabel?: string;
}) {
  return (
    <div className="settings-checkbox">
      <CheckboxField
        value={indeterminate ? null : !!checked}
        onValueChanged={onChange}
        // The visible text is the accessible name; ariaLabel only names a
        // box whose text lives elsewhere (label="").
        label={label || undefined}
        ariaLabel={ariaLabel ?? label}
        disabled={disabled}
      />
      {helpText && <HelpTooltip text={helpText} />}
    </div>
  );
}

/**
 * Single-line text field. Renders the shared `AppTextBox` internally.
 *
 * Was a native `<input className="settings-input">`. Same reason the
 * dropdown and date fields moved: on a form row beside the shared
 * editors the native box sat on a different baseline, and the inline clear
 * button was a hand-rolled adornment rather than the shared field's own.
 *
 * Props API unchanged, so all call sites converted without edits. `type`
 * and `inputMode` are forwarded as real input attributes rather than
 * switching widget: this control's contract is string-in/string-out, and
 * a number box would change that to a number. Callers wanting a
 * true numeric editor already have SettingsNumber.
 *
 * The label follows the label placement setting like every shared field (on
 * the outline by default, above or beside on request): it is passed to the
 * field, which draws it and names the input with it. Help text sits beside
 * the box. An empty `label` (the caller draws its own) keeps the old empty
 * label slot, so those layouts do not move.
 */
export function SettingsInput({ label, srLabel, value, onChange, disabled, placeholder, helpText, maxLength, clearable, required, error, errorText, inputMode, type = 'text', uppercase, onBlur, autoComplete }: {
  label: string;
  /**
   * Accessible name for callers that render their own visible label outside
   * this component and pass `label=""` to suppress the built-in one.
   *
   * That outside <label> has no `htmlFor` to reach this component's generated
   * id, so it labels nothing — and an empty `label` meant no `aria-label`
   * either, leaving the control with NO accessible name. A screen reader
   * announces "edit, blank". On a clinical form that is not friction: the user
   * cannot tell the weight field from the fluid restriction.
   *
   * Names the control without rendering a second visible label.
   */
  srLabel?: string; value: string; onChange: (v: string) => void; disabled?: boolean; placeholder?: string; helpText?: string; maxLength?: number; clearable?: boolean; required?: boolean; error?: boolean; errorText?: string; inputMode?: 'text' | 'numeric' | 'decimal' | 'tel'; type?: 'text' | 'number';
  /**
   * When true, applies `text-transform: uppercase` to the input so typed
   * characters appear uppercased as the user types — matches WinForms'
   * `CharacterCasing = Upper` textbox behavior. Storage-side uppercasing
   * still lives in the caller's `onChange` (SettingsInput doesn't
   * transform the value it passes upward). Mirrors the same-name prop on
   * `SettingsAutoComplete`.
   */
  uppercase?: boolean;
  /** Focus left the field; gets the text in the box (WinForms `Validated`). */
  onBlur?: (value: string) => void;
  /** The input's autocomplete attribute. "off" keeps the browser's saved
   *  address / form entries from popping up over the box (Room, Bed). */
  autoComplete?: string;
}) {
  const id = useId();
  const inputAttr = useMemo(
    () => ({
      id,
      // The name for the box. With a label the field names the input itself and
      // AppTextBox ignores this; kept so screen tests that stand in for the
      // box can still find it by name.
      ...((label || srLabel) ? { 'aria-label': label || srLabel } : {}),
      ...(inputMode ? { inputmode: inputMode } : {}),
      ...(type === 'number' ? { inputmode: inputMode ?? 'numeric' } : {}),
      ...(uppercase ? { style: 'text-transform: uppercase' } : {}),
      ...(autoComplete ? { autoComplete } : {}),
    }),
    [id, label, srLabel, inputMode, type, uppercase, autoComplete],
  );
  return (
    <div className="settings-field">
      {!label && <OuterLabelSlot id={id} required={required} helpText={helpText} />}
      <FieldRow helpText={label ? helpText : undefined}>
        <AppTextBox
          value={value ?? ''}
          onValueChanged={(v) => onChange(maxLength ? v.slice(0, maxLength) : v)}
          label={label || undefined}
          required={!!label && required}
          maxLength={maxLength}
          placeholder={placeholder}
          disabled={disabled}
          error={error}
          showClearButton={!!clearable}
          inputAttr={inputAttr}
          onBlur={onBlur ? (e) => onBlur((e.target as HTMLInputElement).value ?? '') : undefined}
        />
      </FieldRow>
      {error && errorText && <span style={{ color: '#ef4444', fontSize: 'var(--font-size-small)', marginTop: 2 }}>{errorText}</span>}
    </div>
  );
}

/**
 * Free-text field with suggestions. Renders MUI `Autocomplete` in free-text
 * (freeSolo) mode: it offers matches (contains, case-insensitive) but does not
 * constrain to them.
 *
 * Props API unchanged. `uppercase` still uppercases the stored value on
 * change; the visual upper-casing while typing continues to come from the
 * caller's CSS (e.g. `.dh-allergies-upper`), as before.
 *
 * `onChange` fires on each keystroke, a picked suggestion or the clear
 * button, never because `value` changed. The DevExtreme Autocomplete this
 * replaced also fired it when a loaded record changed `value`, which wrote
 * the loaded text straight back as an edit.
 *
 * An empty `label` skips the label slot entirely, so the control can drop
 * into a grid where the caller supplies its own label cell.
 */
export function SettingsAutoComplete({ label, srLabel, value, onChange, suggestions, disabled, helpText, maxLength, placeholder, uppercase, clearable }: {
  label: string;
  /**
   * Accessible name for callers that render their own visible label outside
   * this component and pass `label=""` to suppress the built-in one.
   *
   * That outside <label> has no `htmlFor` to reach this component's generated
   * id, so it labels nothing — and an empty `label` meant no `aria-label`
   * either, leaving the control with NO accessible name. A screen reader
   * announces "edit, blank". On a clinical form that is not friction: the user
   * cannot tell the weight field from the fluid restriction.
   *
   * Names the control without rendering a second visible label.
   */
  srLabel?: string;
  value: string;
  onChange: (v: string) => void;
  suggestions: readonly string[];
  disabled?: boolean;
  helpText?: string;
  maxLength?: number;
  placeholder?: string;
  uppercase?: boolean;
  clearable?: boolean;
}) {
  const id = useId();
  const placement = useLabelPlacement();

  // Callers build `suggestions` inline, so hold the previous array unless
  // the contents actually changed.
  const itemsRef = useRef<string[]>([]);
  const prev = itemsRef.current;
  const unchanged = prev.length === suggestions.length && suggestions.every((v, i) => prev[i] === v);
  if (!unchanged) itemsRef.current = [...suggestions];
  const items = itemsRef.current;

  const text = value ?? '';
  const report = (raw: string) => {
    const cased = uppercase ? raw.toUpperCase() : raw;
    const next = maxLength ? cased.slice(0, maxLength) : cased;
    if (next !== text) onChange(next);
  };

  const outlineLabel = borderLabel(placement, label || undefined);

  return (
    <div className="settings-field">
      <FieldRow helpText={helpText}>
      <Field inputId={id} label={label || undefined} labelPlacement={placement}>
        <MuiAutocomplete<string, false, boolean, true>
          id={id}
          freeSolo
          options={items}
          inputValue={text}
          // Typing, a picked suggestion and the clear button arrive with an
          // event; a `value` change from the caller does not report.
          onInputChange={(e, v) => { if (e) report(v ?? ''); }}
          filterOptions={(opts, state) => {
            const q = state.inputValue.trim().toLowerCase();
            return q ? opts.filter((o) => o.toLowerCase().includes(q)) : opts;
          }}
          disableClearable={!clearable}
          disabled={disabled}
          size="small"
          fullWidth
          renderInput={(params) => (
            <TextField
              {...params}
              label={outlineLabel}
              InputLabelProps={outlineLabel ? { ...params.InputLabelProps, shrink: true } : params.InputLabelProps}
              InputProps={{ ...params.InputProps, notched: !!outlineLabel }}
              placeholder={placeholder}
              inputProps={{
                ...params.inputProps,
                maxLength,
                'aria-label': label ? undefined : srLabel,
              }}
            />
          )}
        />
      </Field>
      </FieldRow>
    </div>
  );
}

/**
 * Date field. Renders the shared `DateField` (typed MM/DD/YYYY plus our own
 * calendar).
 *
 * Was a native `<input type="date">`, then DevExtreme `DateBox`. The props
 * API is unchanged: `value` in and `onChange` out are `yyyy-MM-dd` strings
 * ('' when cleared), so the call sites did not change. A full ISO timestamp
 * as `value` is read as its day.
 *
 * `onChange` fires only when a person changes the date, never when `value`
 * changes (DateBox also fired it for values set in code).
 *
 * The label follows the label placement setting, like the sibling
 * `SettingsInput` / `SettingsDropdown` fields it shares a form grid with.
 */
export function SettingsDateInput({ label, srLabel, value, onChange, disabled, helpText, required, error, errorText, clearable, min, max, rangeErrorText, twoDigitYear }: {
  label: string;
  /**
   * Accessible name for callers that render their own visible label outside
   * this component and pass `label=""` to suppress the built-in one.
   *
   * That outside <label> has no `htmlFor` to reach this component's generated
   * id, so it labels nothing — and an empty `label` meant no `aria-label`
   * either, leaving the control with NO accessible name. A screen reader
   * announces "edit, blank". On a clinical form that is not friction: the user
   * cannot tell the weight field from the fluid restriction.
   *
   * Names the control without rendering a second visible label.
   */
  srLabel?: string; value: string; onChange: (v: string) => void; disabled?: boolean; helpText?: string; required?: boolean; error?: boolean; errorText?: string; clearable?: boolean;
  /** Earliest / latest day, "yyyy-MM-dd". A typed day outside them is flagged. */
  min?: string; max?: string;
  /** Message for a typed day outside min/max, e.g. "Birth date can't be in the future." */
  rangeErrorText?: string;
  /** 'past' reads a two-digit year that would be after today in the 1900s (birth dates). */
  twoDigitYear?: TwoDigitYear;
}) {
  const id = useId();
  const inputAttr = useMemo(() => ({ id }), [id]);
  return (
    <div className="settings-field">
      {!label && <OuterLabelSlot id={id} required={required} helpText={helpText} />}
      <FieldRow helpText={label ? helpText : undefined}>
        <DateField
          value={value || null}
          onValueChanged={(v) => onChange(v ?? '')}
          label={label || undefined}
          showClearButton={clearable}
          disabled={disabled}
          required={required}
          error={error}
          errorText={error && errorText ? errorText : undefined}
          ariaLabel={label ? undefined : srLabel}
          inputAttr={inputAttr}
          min={min}
          max={max}
          rangeErrorText={rangeErrorText}
          twoDigitYear={twoDigitYear}
        />
      </FieldRow>
    </div>
  );
}

/**
 * Time field. Renders the shared `TimeField` (typed "9:30 AM" plus a list of
 * times every 30 minutes, DateBox's default interval).
 *
 * Contract preserved: `value` in and `onChange` out are still the
 * `1900-01-01THH:mm:00` sentinel-date strings the callers store ('' when
 * cleared), and the display is still "9:30 AM". The time part is put back on
 * 1900-01-01 on the way out so a caller that passed a real date in cannot
 * leak that date into storage.
 */
/**
 * A fixed-width box (number, time) whose label is on the outline: the label is
 * clipped to the box (form.css, max-width 100% - 28px), so "Auto Delete
 * Discharged Residents" read "Auto Delete Di..." on a 120px number box. The
 * box grows to show its label whole; above or beside the box the label has
 * its own room and the box keeps its width.
 */
function fixedBoxWidth(box: number, label: string | undefined, placement: LabelPlacement): number {
  if (!label || placement !== 'border') return box;
  // The outline label is 12px regular in the app font (--label-font-size,
  // --font-sans, which the user's Font setting changes); 28px of the box is
  // the label's inset and the notch padding, plus a little slack.
  const family = typeof document !== 'undefined'
    ? getComputedStyle(document.documentElement).getPropertyValue('--font-sans').trim()
    : '';
  return Math.max(box, Math.ceil(measureText(label, `400 12px ${family || 'system-ui, sans-serif'}`) + 34));
}

export function SettingsTimeInput({ label, srLabel, value, onChange, disabled, helpText, clearable = true }: {
  label: string;
  /**
   * Accessible name for callers that render their own visible label outside
   * this component and pass `label=""` to suppress the built-in one.
   *
   * Names the control without rendering a second visible label.
   */
  srLabel?: string; value: string; onChange: (v: string) => void; disabled?: boolean; helpText?: string; clearable?: boolean;
}) {
  const id = useId();
  const inputAttr = useMemo(() => ({ id }), [id]);
  const placement = useLabelPlacement();
  return (
    <div className="settings-field">
      {!label && <OuterLabelSlot id={id} helpText={helpText} />}
      <FieldRow helpText={label ? helpText : undefined} fixed>
        <TimeField
          value={value || null}
          onValueChanged={(t) => onChange(t ? `1900-01-01T${t}:00` : '')}
          label={label || undefined}
          interval={30}
          showClearButton={clearable}
          disabled={disabled}
          width={fixedBoxWidth(160, label, placement)}
          ariaLabel={label ? undefined : srLabel}
          inputAttr={inputAttr}
        />
      </FieldRow>
    </div>
  );
}

/**
 * Numeric field. Renders the shared `NumberField` (MUI) with -/+ buttons.
 *
 * min/max are enforced (an out-of-range number is flagged while typed and
 * clamped when the field is left), ArrowUp / ArrowDown step, and `decimals`
 * pads the shown value (150.00) while still allowing "1.5" mid-typing.
 *
 * Typed numbers are reported when the field is left or Enter is pressed (the
 * DevExtreme NumberBox's timing); the -/+ buttons and arrow keys report at
 * once. `onChange` never fires because `value` changed: the NumberBox did,
 * echoing every loaded value back as an edit.
 *
 * Props API unchanged — still number in, number out — so all call sites
 * converted without edits.
 */
export function SettingsNumber({ label, srLabel, value, min, max, disabled, onChange, step, helpText, suffix, decimals }: {
  label: string; value: number; min?: number; max?: number; disabled?: boolean; onChange: (v: number) => void; step?: number; helpText?: string; suffix?: string;
  /**
   * Accessible name for callers that render their own visible label outside
   * this component and pass `label=""` to suppress the built-in one.
   *
   * That pattern appears ~91 times across the app, and every one of them
   * produced a control with no accessible name at all: the outside `<label>`
   * has no `htmlFor` to point at this component's generated id, so it labels
   * nothing, and an empty `label` meant no `aria-label` either. Screen readers
   * announced a bare spin button.
   *
   * Names the control without rendering a second visible label.
   */
  srLabel?: string;
  /** Pad the value to this many decimal places (e.g. 2 shows `150.00`). */
  decimals?: number;
}) {
  const id = useId();
  const inputAttr = useMemo(() => ({ id }), [id]);
  const placement = useLabelPlacement();
  return (
    <div className="settings-field">
      {!label && <OuterLabelSlot id={id} />}
      <div className="settings-field-row settings-field-row--fixed">
        <NumberField
          value={value ?? 0}
          label={label || undefined}
          onValueChanged={(v) => onChange(v ?? 0)}
          emptyValue={0}
          min={min}
          max={max}
          step={step ?? 1}
          format={decimals != null ? { type: 'fixedPoint', precision: decimals } : undefined}
          showSpinButtons
          commitOnBlur
          disabled={disabled}
          // Room for "1234.56" beside the -/+ buttons when decimals are shown.
          width={fixedBoxWidth(decimals ? 150 : 120, label, placement)}
          // Names the input when the visible label is the caller's (srLabel);
          // the -/+ buttons take the label or this.
          ariaLabel={label ? undefined : srLabel}
          inputAttr={inputAttr}
        />
        {suffix && <span className="settings-field-suffix">{suffix}</span>}
        {helpText && <HelpTooltip text={helpText} />}
      </div>
    </div>
  );
}

/**
 * Dropdown. Renders the shared `AppSelectBox` internally.
 *
 * Was a native `<select className="settings-select">`, which is why a form
 * row mixing this with any shared editor sat on two different baselines:
 * `.settings-select` hard-codes `height: 34px` and ignores `--input-height`,
 * so at the Small or Large text setting it stayed 34px while every shared
 * editor moved to 30px / 42px. Going through AppSelectBox also brings the two things
 * the native control could not do — type-ahead search over long lookups, and
 * a capped popup height.
 *
 * The props API is unchanged, so all call sites converted without edits.
 * Behaviour differences worth knowing:
 *   - Search is on (AppSelectBox's default). Harmless on a 3-item list, and
 *     keeping the default means these match the 200-odd AppSelectBox
 *     instances elsewhere rather than forming a third behaviour.
 *   - Clearing now yields AppSelectBox's `null`, mapped back to the caller's
 *     "unset" sentinel (0 for numeric values, '' for string) so call sites
 *     that treat 0 as empty keep working.
 *
 * As with SettingsInput, the label follows the label placement setting (on
 * the outline by default), so this field stays aligned with the sibling
 * fields it shares a form grid with.
 */
export function SettingsDropdown<V extends string | number = number>({ label, srLabel, value, options, onChange, disabled, helpText, required, error, errorText, clearable, placeholder, optionAdornment }: {
  label: string;
  /**
   * Accessible name for callers that render their own visible label outside
   * this component and pass `label=""` to suppress the built-in one.
   *
   * That outside <label> has no `htmlFor` to reach this component's generated
   * id, so it labels nothing — and an empty `label` meant no `aria-label`
   * either, leaving the control with NO accessible name. A screen reader
   * announces "edit, blank". On a clinical form that is not friction: the user
   * cannot tell the weight field from the fluid restriction.
   *
   * Names the control without rendering a second visible label.
   */
  srLabel?: string;
  value: V;
  options: readonly { value: V; label: string }[];
  onChange: (v: V) => void;
  disabled?: boolean;
  helpText?: string;
  required?: boolean;
  error?: boolean;
  errorText?: string;
  clearable?: boolean;
  placeholder?: string;
  /**
   * Something drawn after an option's text, in the list and in the field
   * (a consistency's IDDSI badge). Return null for options without one; the
   * field then shows its plain text. Keep the function identity stable
   * (module level or useCallback) so the list is not rebuilt every render.
   */
  optionAdornment?: (value: V) => React.ReactNode;
}) {
  const id = useId();
  const clearValue = (typeof value === 'number' ? 0 : '') as V;

  // Most call sites build `options` inline, so the array identity changes on
  // every render. Handing that straight to the select box would make it
  // rebuild its list each time. Keep the previous array unless the contents
  // actually changed.
  const optionsRef = useRef<{ value: V; label: string }[]>([]);
  const prevOptions = optionsRef.current;
  const optionsUnchanged =
    prevOptions.length === options.length
    && options.every((o, i) => prevOptions[i].value === o.value && prevOptions[i].label === o.label);
  if (!optionsUnchanged) {
    optionsRef.current = options.map((o) => ({ value: o.value, label: o.label }));
  }
  const items = optionsRef.current;

  // An option's text, then its adornment. In the field, only options that
  // have an adornment are drawn; the rest keep the plain text.
  const adornedItem = useMemo(() => (optionAdornment
    ? (o: { value: V; label: string }) => (
        <span className="settings-option-adorned">
          <span className="settings-option-adorned-text">{o.label}</span>
          {optionAdornment(o.value)}
        </span>
      )
    : undefined), [optionAdornment]);
  const adornedValue = useMemo(() => (optionAdornment
    ? (o: { value: V; label: string }) => {
        const extra = optionAdornment(o.value);
        return extra == null ? null : (
          <span className="settings-option-adorned">
            <span className="settings-option-adorned-text">{o.label}</span>
            {extra}
          </span>
        );
      }
    : undefined), [optionAdornment]);

  const inputAttr = useMemo(
    // With a label AppSelectBox names the input from it and ignores this;
    // kept so screen tests that stand in for AppSelectBox can find the box.
    () => ({ id, ...((label || srLabel) ? { 'aria-label': label || srLabel } : {}) }),
    [id, label, srLabel],
  );

  return (
    <div className="settings-field">
      {!label && <OuterLabelSlot id={id} required={required} helpText={helpText} />}
      <FieldRow helpText={label ? helpText : undefined}>
        <AppSelectBox
          dataSource={items}
          displayExpr="label"
          valueExpr="value"
          value={value}
          onValueChanged={(v) => onChange((v ?? clearValue) as V)}
          label={label || undefined}
          required={!!label && required}
          placeholder={placeholder}
          // Opt-in, matching the previous behaviour — the native version only
          // drew its clear button when `clearable` was passed.
          showClearButton={!!clearable}
          disabled={disabled}
          isValid={!error}
          inputAttr={inputAttr}
          itemRender={adornedItem}
          renderValue={adornedValue}
        />
      </FieldRow>
      {error && errorText && <span style={{ color: '#ef4444', fontSize: 'var(--font-size-small)', marginTop: 2 }}>{errorText}</span>}
    </div>
  );
}

export function SettingsMultiColDropdown({ label, srLabel, value, options, columns, columnStyles, onChange, disabled, helpText, clearable, searchPlaceholder }: {
  label: string;
  /**
   * Accessible name for callers that render their own visible label outside
   * this component and pass `label=""` to suppress the built-in one.
   *
   * That outside <label> has no `htmlFor` to reach this component's generated
   * id, so it labels nothing — and an empty `label` meant no `aria-label`
   * either, leaving the control with NO accessible name. A screen reader
   * announces "edit, blank". On a clinical form that is not friction: the user
   * cannot tell the weight field from the fluid restriction.
   *
   * Names the control without rendering a second visible label.
   */
  srLabel?: string;
  value: number;
  clearable?: boolean;
  // `cols` accepts React.ReactNode entries — strings still work
  // (backwards-compatible), and JSX can be passed for icon
  // columns like "In MC" indicators. The closed-state caption
  // renders cols[0], so the first column should be plain text
  // or a small inline node.
  // Per-option `style` is applied to the closed-state button
  // when that option is selected — used for color-coded options
  // (e.g. diet types / consistencies carrying their argb_Back +
  // argb_Fore from reference data). The open list rows stay
  // neutral so the popup remains readable.
  options: readonly { value: number; cols: React.ReactNode[]; style?: React.CSSProperties }[];
  columns: string[];
  // Optional per-column CSS — useful for constraining a fixed
  // column (e.g. an "In MC" check column) so it doesn't grow
  // proportionally to the rest of the table. Applied to a
  // `<colgroup><col>` per column so both `<th>` and `<td>`
  // inherit the constraint without duplication.
  columnStyles?: (React.CSSProperties | undefined)[];
  onChange: (v: number) => void;
  disabled?: boolean;
  helpText?: string;
  // Optional override for the search box placeholder. Defaults
  // to "Type to filter..."; callers pass "Search..." when they
  // want the shorter/more-familiar wording.
  searchPlaceholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const placement = useLabelPlacement();
  const buttonId = useId();
  // On the outline (the default placement) the label sits in a notch of the
  // button's border, drawn the way MUI draws it for the other fields: a
  // fieldset whose legend carries the label text invisibly to size the gap.
  const onBorder = label !== '' && placement === 'border';
  const ref = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const triggerWrapRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  // Portal-anchored popup coordinates. Computed from the
  // trigger's getBoundingClientRect so the popup overlays the
  // page in viewport-fixed space — necessary to escape MUI
  // Dialog's scroll container, which would otherwise clip the
  // popup and push the dialog content when it opens.
  const [popupPos, setPopupPos] = useState<{ top: number; left: number; width: number } | null>(null);

  // Outside-click closes the popup. Both the trigger (`ref`) and
  // the portaled popup (`popupRef`) need to be exempt so an
  // in-popup click doesn't immediately close it.
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t)) return;
      if (popupRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Recompute popup position whenever it opens, on scroll, or on
  // resize. `useLayoutEffect` so the initial render lands at the
  // right coords without a flicker.
  useLayoutEffect(() => {
    if (!open) { setPopupPos(null); return; }
    const compute = () => {
      const el = triggerWrapRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setPopupPos({
        top: rect.bottom + 2,
        left: rect.left,
        width: rect.width,
      });
    };
    compute();
    window.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', compute, true);
      window.removeEventListener('resize', compute);
    };
  }, [open]);

  // Reset the filter every time the popup closes so reopening
  // starts fresh; auto-focus the search input when it opens.
  useEffect(() => {
    if (open) {
      // Defer to after the popup renders.
      const id = requestAnimationFrame(() => searchInputRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
    setQuery('');
  }, [open]);

  const selected = options.find((o) => o.value === value);
  const displayText = selected?.cols[0] ?? '';

  // Contains-filter on the first column when it's a plain
  // string (case-insensitive). Options whose cols[0] isn't a
  // string (e.g. JSX) always pass through unfiltered. An empty
  // query passes everything.
  const filteredOptions = (() => {
    const q = query.trim().toLowerCase();
    if (q === '') return options;
    return options.filter((o) => {
      const first = o.cols[0];
      if (typeof first !== 'string') return true;
      return first.toLowerCase().includes(q);
    });
  })();

  return (
    <div className="settings-field" ref={ref} style={{ position: 'relative' }}>
      {/* Empty `label` suppresses the label entirely so the editor doesn't
          reserve vertical space for an invisible caption: used when an
          external header (e.g. a column heading above a grid of editors) is
          already labeling the field. Otherwise the label follows the label
          placement setting, like every shared field. */}
      <FieldRow helpText={helpText}>
      <Field inputId={buttonId} label={label || undefined} labelPlacement={placement}>
      <div ref={triggerWrapRef} style={{ display: 'flex', alignItems: 'stretch', gap: 0, position: 'relative', width: '100%' }}>
        <span className={onBorder ? 'settings-multicol-outlined' : 'settings-multicol-plain'}>
        <button
          id={buttonId}
          type="button"
          className="settings-select settings-multicol-btn"
          disabled={disabled}
          // This is a custom portal dropdown, not a shared field, so
          // there is no inputAttr to carry the name — the trigger takes it
          // directly. Without it the control announces only its current value
          // ("Regular"), with nothing to say which field that value belongs to.
          aria-label={label || srLabel}
          aria-expanded={open}
          aria-haspopup="listbox"
          onClick={() => setOpen(!open)}
          style={{
            flex: 1,
            minWidth: 0,
            opacity: disabled ? 0.5 : 1,
            // Selected option's own style (e.g. argb-derived
            // foreground/background) flows onto the closed
            // button so color-coded options like diet types
            // remain visually distinct even when collapsed.
            ...(selected?.style ?? {}),
            // When a Clear button is attached on the right,
            // square off the editor's right corners so the two
            // join cleanly.
            ...(clearable && selected && !disabled
              ? { borderTopRightRadius: 0, borderBottomRightRadius: 0 }
              : {}),
          }}
        >
          <span>{displayText}</span>
          <i className="bi bi-chevron-down" style={{ marginLeft: 'auto', fontSize: '0.7em' }} />
        </button>
        {onBorder && (
          <>
            <label className="settings-multicol-outline-label" htmlFor={buttonId}>{label}</label>
            <fieldset className="settings-multicol-outline" aria-hidden="true">
              <legend><span>{label}</span></legend>
            </fieldset>
          </>
        )}
        </span>
        {clearable && selected && !disabled && (
          <button
            type="button"
            className="settings-spin-btn settings-input-adornment"
            style={{ flexShrink: 0, width: 28, borderRadius: '0 6px 6px 0', border: '1px solid #d1d5db', borderLeft: 'none', color: '#ef4444', background: 'var(--card-bg, #fff)' }}
            onClick={() => onChange(0)}
            aria-label="Clear"
          >
            <i className="bi bi-x" />
          </button>
        )}
      </div>
      {open && popupPos && createPortal(
        <div
          ref={popupRef}
          className="settings-multicol-popup"
          // Override the absolute-positioned defaults from the
          // CSS — portal mounts at document.body so the popup
          // floats in viewport-fixed space, escaping any
          // overflow:auto ancestor (e.g. MUI Dialog content).
          style={{
            position: 'fixed',
            top: popupPos.top,
            left: popupPos.left,
            width: popupPos.width,
            minWidth: popupPos.width,
          }}
        >
          {/* Typeahead search — contains-match against the
              first column. Empty input shows all rows. */}
          <div style={{ padding: '4px 6px 6px 6px', borderBottom: '1px solid var(--border-color, #e5e7eb)' }}>
            <input
              ref={searchInputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder ?? 'Type to filter...'}
              className="settings-input"
              style={{ width: '100%', height: 28, fontSize: 'var(--font-size-small)' }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setOpen(false);
                } else if (e.key === 'Enter' && filteredOptions.length > 0) {
                  // Convenient pick — Enter selects the first
                  // surviving option (or keeps current
                  // selection if it survives).
                  const target = filteredOptions.find((o) => o.value === value) ?? filteredOptions[0];
                  onChange(target.value);
                  setOpen(false);
                }
              }}
            />
          </div>
          <table className="settings-multicol-table">
            {columnStyles && (
              <colgroup>
                {columns.map((_, i) => (
                  <col key={i} style={columnStyles[i]} />
                ))}
              </colgroup>
            )}
            <thead>
              <tr>
                {/* Per-column style also applied to <th> so
                    text alignment (and any other inline CSS)
                    flows through to the header — <col> in the
                    colgroup only carries width-class properties,
                    not text-align. */}
                {columns.map((col, i) => (
                  <th key={i} style={columnStyles?.[i]}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredOptions.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    style={{ padding: '8px', textAlign: 'center', color: 'var(--text-secondary, #888)', fontStyle: 'italic' }}
                  >
                    No matches
                  </td>
                </tr>
              ) : (
                filteredOptions.map((o) => (
                  <tr
                    key={o.value}
                    className={o.value === value ? 'selected' : ''}
                    onClick={() => { onChange(o.value); setOpen(false); }}
                    // Per-option `style` is intentionally NOT applied
                    // to the open list rows — only to the closed
                    // editor button up above. Keeps the popup
                    // readable with the default neutral background.
                  >
                    {o.cols.map((c, i) => (
                      <td key={i} style={columnStyles?.[i]}>{c}</td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>,
        document.body,
      )}
      </Field>
      </FieldRow>
    </div>
  );
}

export function SettingsColorPicker({ label, argbValue, onChange }: {
  label: string; argbValue: number; onChange: (argb: number) => void;
}) {
  const id = useId();
  const hexDisplayId = `${id}-hex`;
  return (
    <div className="settings-field" style={{ alignItems: 'center' }}>
      <label className="settings-field-label" htmlFor={id}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          id={id}
          type="color"
          value={argbToHex(argbValue)}
          onChange={(e) => onChange(hexToArgb(e.target.value))}
          aria-describedby={hexDisplayId}
          style={{ width: 40, height: 28, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', padding: 0 }}
        />
        <span id={hexDisplayId} style={{ fontSize: '0.8rem', color: '#64748b', fontFamily: 'monospace' }}>
          {argbToHex(argbValue)}
        </span>
      </div>
    </div>
  );
}

export function SettingsDivider({ children }: { children: React.ReactNode }) {
  return <div className="settings-divider">{children}</div>;
}

/** A titled group of settings: a plain heading over a rule. */
export function SettingsCard({ title, children, helpText }: {
  title: string; children: React.ReactNode; helpText?: string;
}) {
  return (
    <div className="settings-card">
      <div className="settings-card-header">
        <h5>
          {title}
          {helpText && <HelpTooltip text={helpText} />}
        </h5>
      </div>
      <div className="settings-card-body">
        {children}
      </div>
    </div>
  );
}

export function SettingsGrid({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8 }}>
      {children}
    </div>
  );
}
